import { Download } from "lucide-react";

const timeline = [
  ["2021 — now", "Independent developer", "Building products and sites for founders, studios, and small in-house teams."],
  ["2018 — 2021", "Senior engineer, Northgate Labs", "Led the frontend platform team; shipped a design system used by 30 engineers."],
  ["2015 — 2018", "Developer, Studio Ferro", "Marketing sites, e-commerce builds, and a lot of performance work."],
];

export default function About() {
  return (
    <div className="mx-auto max-w-6xl px-6 pt-16 sm:px-10 sm:pt-24" data-testid="about-page">
      <p className="font-mono-label mb-5">About</p>
      <h1 className="max-w-3xl text-4xl font-extrabold leading-[1.05] sm:text-5xl">
        Eleven years of shipping on the web, mostly for small teams.
      </h1>

      <div className="mt-16 grid gap-14 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-6 text-base leading-relaxed text-foreground/85 sm:text-lg">
          <p>
            I'm a web developer based in the United States, working remotely with
            clients across the country. My work sits at the intersection of
            engineering and editorial design: I care as much about how a sentence
            reads as how a query performs.
          </p>
          <p>
            Most engagements start with a two-week discovery, then move into
            focused build sprints. I write the frontend, the API, and the
            deployment pipeline, which means fewer handoffs and fewer surprises.
            I've done this for invoicing tools, publishing platforms, offline
            field apps, and a coffee roaster who needed a faster storefront.
          </p>
          <p>
            Outside of client work I maintain a small open-source component
            library, write about performance budgets, and take on one pro bono
            project a year for a local nonprofit.
          </p>

          <div className="pt-4">
            <a
              href="/john-doe-resume.pdf"
              download
              data-testid="about-resume-download"
              className="inline-flex items-center gap-2 rounded-sm border border-foreground px-6 py-3 text-sm font-medium transition-colors hover:bg-foreground hover:text-background"
            >
              <Download size={16} /> Download resume (PDF)
            </a>
          </div>
        </div>

        <aside className="space-y-10">
          <img
            src="https://images.unsplash.com/photo-1625929664135-197db5f6c857?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            alt="John Doe"
            loading="lazy"
            className="w-full border border-border object-cover grayscale"
            style={{ aspectRatio: "4 / 5" }}
          />
          <div>
            <p className="font-mono-label mb-4">Toolkit</p>
            <div className="flex flex-wrap gap-2">
              {["React", "TypeScript", "Python", "FastAPI", "Postgres", "MongoDB", "Tailwind", "AWS", "Playwright"].map((t) => (
                <span key={t} className="border border-border px-2.5 py-1 text-xs text-muted-foreground">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </aside>
      </div>

      <section className="mt-24 border-t border-border pt-12" data-testid="about-timeline">
        <p className="font-mono-label mb-8">Experience</p>
        <div className="divide-y divide-border">
          {timeline.map(([when, role, detail]) => (
            <div key={when} className="grid gap-2 py-7 sm:grid-cols-[160px_1fr] sm:gap-8">
              <p className="font-mono-label pt-1">{when}</p>
              <div>
                <h3 className="text-base font-bold sm:text-lg">{role}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{detail}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
